'use client';
// What the web shows when the daemon answers `licence_locked`: the installation has no usable
// licence, so there is nobody to sign in as and the shell must stay closed — exactly like the
// not-yet-installed case (`SetupPending`). The visitor either activates a key here or contacts
// support; on success the daemon restarts, so this page polls `setupStatus` until the lock lifts
// and then reloads into the login form.
import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/shadcn/card';
import { ErrorState, LoadingState, Spinner } from '../ui/states';
import { apiErrorMessage, elowenClient, type LicenceStatusView } from '../../lib/elowenClient';
import { CopyableId, LICENCE_QUERY_KEY, LicenceActivateForm } from '../licence/LicenceControls';
import { interpolate, useTranslation } from '../../lib/i18n';
import type { LocaleDict } from '../../lib/i18n/types';
import { localDateTime } from '../../lib/format';
import { SUPPORT_URL } from '../../lib/productLinks';
import { ChettyLogo } from './ChettyLogo';

type LockedReason = NonNullable<LicenceStatusView['reason']>;

function reasonText(reason: LockedReason | null, t: LocaleDict): string {
  switch (reason) {
    case 'missing': return t.licence.reasonMissing;
    case 'expired': return t.licence.reasonExpired;
    case 'offline': return t.licence.reasonOffline;
    case 'refused': return t.licence.reasonRefused;
    case 'wrongInstance': return t.licence.reasonWrongInstance;
    default: return t.licence.reasonInvalid;
  }
}

function InstanceIdRow({ instanceId }: { instanceId: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-w-0 items-center gap-2 rounded-md border border-border bg-sunken px-2.5 py-1.5">
      <span className="flex-1 shrink-0 text-eyebrow uppercase text-muted-foreground">{t.licence.instanceId}</span>
      <CopyableId value={instanceId} copyLabel={t.licence.copyInstanceId} />
    </div>
  );
}

const formatTime = (ms: number | null, never: string, locale: string): string =>
  ms === null ? never : localDateTime(new Date(ms).toISOString(), locale);

export function LicenceLocked() {
  const { t, locale } = useTranslation();
  const status = useQuery({
    queryKey: LICENCE_QUERY_KEY,
    queryFn: elowenClient.licenceStatus,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const [restarting, setRestarting] = useState(false);

  // One bounded setup poll whenever this page is on screen: after our own activation the daemon
  // restarts underneath us, and while we wait another admin may unlock it remotely. Either way the
  // lock lifting is observed here — no manual reload. Failed polls are the restart itself: keep going.
  const waiting = restarting || status.data !== undefined;
  useEffect(() => {
    if (!waiting) return;
    let alive = true;
    const tick = async (): Promise<void> => {
      try {
        const s = await elowenClient.setupStatus();
        if (alive && !s.licenceLocked) window.location.reload();
      } catch { /* still restarting / unreachable */ }
    };
    void tick();
    const timer = window.setInterval(() => { void tick(); }, 2000);
    return () => { alive = false; window.clearInterval(timer); };
  }, [waiting]);

  // The lock lifted under us (another admin activated): this page has nothing left to say.
  const unlocked = status.data?.state === 'active';

  const page = (children: ReactNode) => (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md">
        {children}
      </Card>
    </div>
  );

  if (status.isPending || unlocked) {
    return page(<CardContent><LoadingState variant="block" /></CardContent>);
  }
  if (status.isError) {
    return page((
      <CardContent>
        <ErrorState message={apiErrorMessage(status.error)} onRetry={() => { void status.refetch(); }} />
      </CardContent>
    ));
  }

  const view = status.data;
  if (restarting) {
    return page((
      <CardContent className="flex items-center justify-center gap-2 py-10">
        <Spinner size="sm" label={t.licence.restarting} />
        <span className="text-meta text-muted-foreground">{t.licence.restarting}</span>
      </CardContent>
    ));
  }

  return page((
    <>
      <CardHeader className="items-center text-center">
        <ChettyLogo className="mx-auto w-48" />
        <CardTitle><h1 className="text-eyebrow uppercase text-muted-foreground">{t.licence.pageTitle}</h1></CardTitle>
        <CardDescription>{reasonText(view.reason, t)}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <InstanceIdRow instanceId={view.instanceId} />
        <dl className="flex flex-col gap-1 text-ui">
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-muted-foreground">{t.licence.serverTime}</dt>
            <dd className="text-small tabular-nums">{formatTime(view.lastServerTime, t.licence.neverContacted, locale)}</dd>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-muted-foreground">{t.licence.localTime}</dt>
            <dd className="text-small tabular-nums">{formatTime(view.localTime, t.licence.neverContacted, locale)}</dd>
          </div>
        </dl>
        <LicenceActivateForm onSuccess={() => setRestarting(true)} />
        <p className="text-center text-meta text-muted-foreground">
          {interpolate(t.licence.support, { url: SUPPORT_URL })}
        </p>
      </CardContent>
    </>
  ));
}
